import { html, hydrate } from 'wirejs-dom/v2';
import { Article } from '../../../../../layouts/index.js';
import { CodeBlock } from '../../../../../components/code-block.js';
import { DosMonitor, monitorSvg } from '../../../../../components/dos-monitor.js';
import { randomColor } from '../../../../../lib/color.js';

const title = 'Playing with HTML canvas';
const description = `Drawing primitive low-resolution things on canvas in remembrance of my QuickBASIC years.`;
const tags = ['html', 'typescript', 'javascript', 'canvas', 'graphics', 'retro', 'play'];
const published = new Date('2025-04-10 10:40 PM');

const DOT_INTERVAL = 5;

function randomCoord({ context }: { context: CanvasRenderingContext2D }) {
	return [
		Math.random() * context.canvas.width,
		Math.random() * context.canvas.height
	];
}

const content = () => html`<div id='learning-canvas'>
	<p>When I was first learning to code in grade school, I learned how make some very simple graphics using <a href='https://en.wikipedia.org/wiki/QBasic'>QuickBASIC</a>. One of my first learning exercises was to draw random dots and lines on the screen at a <a href='https://en.wikipedia.org/wiki/Mode_13h'>stunning resolution of 320 x 200 in a magnificent 256 colors</a>. Later, I would also make some "art" with overlapping gradients, mathematical patterns, etc..</p>
	<p>In the modern<span class='muted'>(-ish)</span> day, I haven't really spent much time learning how to make graphics in the browser. Even <a href='https://www.thepointless.com/apps/shooty-ship/'>Shooty Ship</a>, for example, is made entirely with hand-drawn graphics and DOM Elements. <span class='muted'>(Hard to believe. <i>I know.</i>)</span></p>
	<p>So, it's finally time to repeat this experiment with the <a href='https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API'>Canvas API</a>.</p>
	<p>Naturally, I'll start by creating the most important component: <b>The monitor,</b> which I dub the "Interweb VGA".</p>
	${CodeBlock({ language: 'svg', code: monitorSvg })}
	<p>Without anything on it, it looks like this:</p>
	${DosMonitor()}
	<p>After laying a canvas over the monitor SVG, I'll add some methods to wrap the Canvas API. This will just make drawing lines and pixels a little simpler. <span class='muted'>(Strictly speaking, we won't draw individual "pixels" on canvas; we'll draw 1x1 rectangles)</span>. The raw methods will come in handy when I play with image manipulation later; but, for my little experiment, these "pixel" (or <i>point</i>) and "line" abstractions are sufficient.</p>
	<p>Those extension methods look lke this:</p>
	${CodeBlock({
		language: 'typescript',
		code: `
			draw: {
				get context() {
					ctx = ctx ?? self.data.canvas.getContext('2d')!;
					return ctx;
				},

				point(x: number, y: number, color: string) {
					// points are colors by drawing a 1x1 rectangle
					const ctx = this.context;
					ctx.fillStyle = color;
					ctx.fillRect(x, y, 1, 1);
				},
				
				line(x1: number, y1: number, x2: number, y2: number, color: string) {
					// lines are made by walking a path and stroking it with color
					const ctx = this.context;
					ctx.strokeStyle = color;
					ctx.lineWidth = 1;
					ctx.beginPath();
					ctx.moveTo(x1, y1);
					ctx.lineTo(x2, y2);
					ctx.stroke();
				},
			}
		`
	})}
	<p>Nothing here is terribly complicated. This "draw" object will be attached to a component whose "canvas" property is an HTML canvas. And, I'll use the component's <code>onadd()</code> callback to start drawing as soon as the component is added to the DOM. But, before getting to the demo code, let's also cover a couple utilities for generating random coordinates and colors. They're pretty small, but I they would be verbose and ugly enough in-line to warrant being separate utils.</p>
	${CodeBlock({
		language: 'typescript',
		code: `
			function randomCoord({ context }: { context: CanvasRenderingContext2D }) {
				return [
					Math.random() * context.canvas.width,
					Math.random() * context.canvas.height
				];
			}
			
			function randomColor() {
				const letters = '0123456789ABCDEF';
				let color = '#';
				for (let i = 0; i < 6; i++) {
					color += letters[Math.floor(Math.random() * 16)];
				}
				return color;
			}
		`
	})}
	<p>Again, these aren't complicated. But, these adapters make drawing things pretty simple. The "random dots" program becomes <i>very</i> simple.</p>
	${CodeBlock({
		language: 'typescript',
		code: `
			\${DosMonitor().onadd(self => {
				setInterval(() => {
					const coord = randomCoord(self.draw);
					const color = randomColor();
					self.draw.point(coord[0], coord[1], color);
				}, ${DOT_INTERVAL};
			}
		`
	})}
	<p>It looks like this.</p>
	${DosMonitor().onadd(self => {
		setInterval(() => {
			const coord = randomCoord(self.draw);
			const color = randomColor();
			self.draw.point(coord[0], coord[1], color);
		}, DOT_INTERVAL);
	})}
	<p>Producing random lines is about as simple. I'll make the lines interconnect as they're drawn, which increases the complexity slightly. But, even with this small added complexity, it's still pretty simple.</p>
	${CodeBlock({
		language: 'typescript',
		code: `
			\${DosMonitor().onadd(self => {
				let lastCoord = randomCoord(self.draw);
				setInterval(() => {
					const nextCoord = randomCoord(self.draw);
					const color = randomColor();
					self.draw.line(lastCoord[0], lastCoord[1], nextCoord[0], nextCoord[1], color);
					lastCoord = nextCoord;
				}, 100);
			})}
		`
	})}
	<p>It ends up looking like this.</p>
	${DosMonitor().onadd(self => {
		let lastCoord = randomCoord(self.draw);
		setInterval(() => {
			const nextCoord = randomCoord(self.draw);
			const color = randomColor();
			self.draw.line(lastCoord[0], lastCoord[1], nextCoord[0], nextCoord[1], color);
			lastCoord = nextCoord;
		}, 100);
	})}
	<p>Now, when I did this originally in grade school with QuickBASIC, I was learning the absolute fundamentals of programming at the same time. I am now arguably a well seasoned "senior" engineer. Some of the abstractions, though maybe not 100% necessary, came very naturally to me. Algorithmically, there's nothing at all to speak of. But, for a grade school brain, I would have struggled a bit to connect some of the dots. <span class='muted'>(See what I did there?)</span></p>
	<p>Today, I also work in a sea of engineers. My <strike>Twitter</strike> <i>X</i> and LinkedIn feeds are flooded with programming and software engineering threads, opinions, and technical rants. We have LLM's that can code nearly anything in an instant &mdash; <i>bugs included!</i> Coloring a few pixels on the screen is pretty unimpressive. <span class='muted'>(Within the last few months, "vibe coders" on Twitter have even created full multi-player flight simulators in a matter of <I>days</i>.)</span></p>
	<p>So, perhaps it's needless to say, but, small adventures like this are very different endeavors now. They no longer feel quite so novel and exciting. But, taking a break from the day-to-day routine of writing complex TypeScript libraries, CI/CD pipelines, and operational dashboards to draw a few pixels and lines on a nostalgic looking "Interweb VGA" has been ... <i>nice</i>.</p>
	<p>If you <i>also</i> like writing code, but find that your day job requires you to argue more about whether to use a <code>for-in</code> or a <code>.forEach()</code> or fix broken deployments ... I encourage you to <i>take a break</i>.</p>
	<p>Take a break to write some code you don't have to debate in a pull request. Take a break to write some code that won't get stuck in a failing deployment pipeline. Take a break to write some code that you don't actually need monitoring and alarming for.</p>
	<p>Take a break.</p>
	<p>Take a break to draw some pixels on an "Interweb VGA", for example.</p>
	<p>You don't need to draw the same pixels I did. Just make some pixels happen. Unnecessary pixels. Playful pixels.</p>
	<p>Take a break.</p>
	<p><i>Play.</i></p>
</div>`;

export const generate = async () => Article({
	title,
	content: content(),
	description,
	tags,
	published
});

export function onload() {
	hydrate('learning-canvas', content as any);
}